import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  showSecurityWarning: boolean;
  setShowSecurityWarning: (show: boolean) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = React.memo(({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);

  // Sistema de atualização automática
  useEffect(() => {
    const checkForUpdates = async () => {
      try {
        const response = await fetch('/version.json?' + Date.now());
        const data = await response.json();
        const currentVersion = localStorage.getItem('app-version');
        
        if (currentVersion && currentVersion !== data.version) {
          toast.success('📅 Calendário econômico atualizado! Recarregando...', {
            duration: 3000,
            style: {
              background: 'linear-gradient(45deg, #bf953f, #fcf6ba)',
              color: '#000',
              fontWeight: 'bold'
            }
          });
          
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        
        localStorage.setItem('app-version', data.version);
      } catch (error) {
        console.log('Version check failed:', error);
      }
    };

    // Verificar atualizações a cada 30 segundos
    const interval = setInterval(checkForUpdates, 30000);
    
    // Verificar imediatamente
    checkForUpdates();
    
    return () => clearInterval(interval);
  }, []);
  // Memoized callbacks for better performance
  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  useEffect(() => {
    let mounted = true;
    
    // Check active sessions
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted) {
          setUser(session?.user ?? null);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error getting session:', error);
        if (mounted) {
          setUser(null);
          setLoading(false);
        }
      }
    };
    
    getSession();

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Mostrar aviso de segurança APENAS no login
        if (event === 'SIGNED_IN' && session?.user) {
          const warningShown = sessionStorage.getItem('security-warning-shown');
          if (!warningShown) {
            setShowSecurityWarning(true);
            sessionStorage.setItem('security-warning-shown', 'true');
          }
        }
        
        // Limpar flag quando fizer logout
        if (event === 'SIGNED_OUT') {
          sessionStorage.removeItem('security-warning-shown');
          setShowSecurityWarning(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value = React.useMemo(() => ({
    user, 
    loading, 
    showSecurityWarning, 
    setShowSecurityWarning, 
    signIn, 
    signOut 
  }), [user, loading, showSecurityWarning, signIn, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
});

AuthProvider.displayName = 'AuthProvider';

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}